import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { getEntity } from '@/services/entities.service'

/**
 * Live access flags for the active entity, used to gate account-level
 * subscription/feature switches (e.g. `accessCustomCourses`).
 *
 * Reads the entity itself rather than the user's cached login profile so it:
 *  - works for Xverse Admins, who have no member role to read the flag from, and
 *  - reflects changes for already-logged-in users without a re-login.
 *
 * Fetches on mount (i.e. on each page load / navigation) and on the ['entity', id]
 * cache invalidation fired when an account is saved. It intentionally does NOT poll
 * or refetch on window focus: those background auth requests can trip a mid-session
 * token refresh (which reloads embedded content like the Articulate player). A flag
 * flipped by a RescueX Admin reaches other open sessions on their next page load.
 *
 * `keepPreviousData` matters here (RSCX-385): `userHasAccess` reads a missing
 * `entityAccess` as "the account doesn't have this feature", so any moment the
 * query holds no data, every access-gated nav item disappears — including for
 * admins, since that gate runs before the Xverse-admin bypass. Saving an account
 * invalidates this key, and without a placeholder the refetch emptied the flags
 * and the admin menu vanished until the user reloaded the page. Holding the last
 * known flags through a refetch keeps the menu stable.
 *
 * @param {string|null|undefined} entityId - the active entity id
 * @returns {{ entityAccess: object|null, isEntityAccessLoading: boolean,
 *   isEntityAccessResolved: boolean }} `isEntityAccessResolved` is false only
 *   while the flags for THIS entity have not landed — either they've never
 *   loaded, or a switch is in flight and the cache still holds the previous
 *   entity's. Callers must treat that as "unknown", not as "denied".
 */
export function useEntityAccess(entityId) {
  const { data, isLoading } = useQuery({
    queryKey: ['entity', entityId],
    queryFn: () => getEntity(entityId),
    enabled: Boolean(entityId),
    staleTime: 0,
    placeholderData: keepPreviousData,
  })

  // keepPreviousData holds the *previous* entity's payload while a switch is in
  // flight. Serving those flags would gate the new account against the old
  // account's features, so only trust data that belongs to the entity asked
  // for. Within one entity this still carries the flags through a refetch,
  // which is the point.
  const isForActiveEntity = Boolean(data) && data.id === entityId

  return {
    entityAccess: isForActiveEntity ? data : null,
    isEntityAccessLoading: Boolean(entityId) && isLoading,
    isEntityAccessResolved: !entityId || isForActiveEntity,
  }
}
